import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatTooltip, MatTooltipModule, TooltipPosition } from '@angular/material/tooltip';
import { getIsMobileOrTablet } from '../../utils/device.util';

/**
 * Small "(i)" icon that explains the field next to it (CWS-1418).
 *
 * Desktop: the explanation shows on hover and on keyboard focus. Touch: a tap
 * opens it and a second tap, a tap anywhere else, or Escape closes it.
 * Material's own long-press gesture is switched off so a plain tap works.
 *
 * The bubble is rendered through the CDK overlay, so it is never clipped by a
 * scrolling table wrapper or a card with `overflow: hidden`.
 *
 * @component
 * @selector app-info-tip
 * @example
 *   <span class="kpi__label">
 *     Net Sales
 *     <app-info-tip label="Net Sales" [text]="tips.netSales"></app-info-tip>
 *   </span>
 *
 * @input {string} text - The explanation. Nothing renders when empty, so a
 *   missing definition never leaves a dead icon behind.
 * @input {string} label - Name of the field, used for the button's aria-label.
 * @input {TooltipPosition} position - Where the bubble opens; defaults to above.
 */
@Component({
  selector: 'app-info-tip',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './info-tip.component.html',
  styleUrl: './info-tip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoTipComponent {
  @Input() text = '';
  @Input() label = '';
  @Input() position: TooltipPosition = 'above';

  get ariaLabel(): string {
    return this.label ? `About ${this.label}` : 'More information';
  }

  /**
   * Hover already opened the bubble on a mouse device, so a click there is a
   * no-op. On touch there is no hover: the tap toggles it.
   */
  onClick(event: Event, tip: MatTooltip): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isTouch()) {
      tip.toggle();
    } else {
      tip.show(0);
    }
  }

  protected isTouch(): boolean {
    return typeof window !== 'undefined' && getIsMobileOrTablet();
  }
}
